import { useContentMotion } from "../lib/useContentMotion";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../components/Icon";
import { effectiveTheme, useAppearance } from "../lib/appearance";
import { mediaOrigin } from "../lib/surfaceResources";
import { ErrorState, LoadingState } from "../shell/ui";
import type { Publication } from "../platform/api";
import { asVisitor, platformRequest, reportViewing } from "../platform/api";
import { buildSrcdoc } from "./surfaceBridge";
import "./PublicSurface.css";

/** Seconds of film after which the player first tells Square, so a viewer who leaves at once still counts. */
const PLAYED_A_LITTLE = 1;
const tenths = (seconds: number) => Math.round(seconds * 10) / 10;

/** Where a work is shown: a Square card, its own page, or the Owner's check before publishing. */
export type SurfaceMode = "feed" | "page" | "preview";

type SurfaceProps = {
  publication: Publication;
  mode?: SurfaceMode;
  /** The work is on screen. A page hears this through the bridge. */
  visible?: boolean;
  /** Feed media: this is the work the viewer is on. */
  playing?: boolean;
  /** Feed media: hold no source at all, so nothing keeps downloading. */
  release?: boolean;
  /** Feed media: the viewer's sound choice, carried from work to work by the feed. */
  sound?: boolean;
  onSound?: (on: boolean) => void;
  /** Feed media: where the page puts playback and sound controls. */
  controls?: HTMLElement | null;
  previewAssetBase?: string;
  /** Preview only: the work was confirmed and is public now, so it is no longer a preview. */
  published?: boolean;
  onReadyChange?: (ready: boolean) => void;
};

/** A film or a sound file plays in the Hub's own player; any other entry is a page. */
export function mediaKind(entry: string | undefined): "video" | "audio" | null {
  if (/\.(mp4|webm)$/i.test(entry || "")) return "video";
  if (/\.(mp3|m4a|wav)$/i.test(entry || "")) return "audio";
  return null;
}

export function PublicSurface(props: SurfaceProps) {
  const kind = mediaKind(props.publication.entry);
  // One player per work: what a viewer watched of one film never carries over to the next.
  return kind ? <PublicMedia key={props.publication.id} {...props} kind={kind} /> : <PublicDocument {...props} />;
}

function WorkProblem({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="public-surface-error" role="alert">
    <p>作品暂时遇到问题：{message}</p>
    <button type="button" className="btn" onClick={onRetry}>重新打开作品</button>
  </div>;
}

/**
 * A film or a sound file, played by a media element in the Hub page itself.
 *
 * The source is the Hub's own asset URL and a media element runs no
 * work-authored script, so there is no frame, bridge or saved progress here.
 * The player tells Square how far a visitor got, once at each stop: when the
 * film has played a little, when it ends, and when it stops being played.
 */
function PublicMedia({
  publication, kind, mode = "page", playing = false, release = false, sound = false, onSound, controls,
  previewAssetBase, published = false, onReadyChange,
}: SurfaceProps & { kind: "video" | "audio" }) {
  const feed = mode === "feed";
  const player = useRef<HTMLMediaElement | null>(null);
  const bind = useCallback((node: HTMLMediaElement | null) => { player.current = node; }, []);
  const [retry, setRetry] = useState(0);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [muted, setMuted] = useState(true);
  // The browser would not start the film without a tap.
  const [held, setHeld] = useState(false);
  const [paused, setPaused] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(() => feed && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches));
  // A viewer's choice belongs to this publication, not to a catalogue response.
  const playbackChoice = useRef<boolean | null>(null);
  const playbackAttempt = useRef(0);
  const assets = mode === "preview" ? previewAssetBase : `/api/public/works/${encodeURIComponent(publication.id)}/assets/`;
  const source = assets ? assets + (publication.entry || "").split("/").map(encodeURIComponent).join("/") : "";
  const hold = Boolean(source) && !(feed && release);
  // Square counts visitors: a preview and the owner's own work tell it nothing.
  const counting = useRef({ id: publication.id, counted: false });
  counting.current = { id: publication.id, counted: mode !== "preview" && !publication.mine };
  // How far this viewer got, and what Square last heard of it.
  const watched = useRef({ furthest: 0, ended: false, toldAt: 0, toldEnded: false });

  /** A stop: tell Square how far the viewer got, unless it has already heard as much. */
  const tell = useCallback((element: HTMLMediaElement | null, leaving = false) => {
    const { id, counted } = counting.current;
    const seen = watched.current;
    const length = element ? element.duration : NaN;
    if (!element || !counted || !Number.isFinite(length) || length <= 0) return;
    seen.furthest = Math.max(seen.furthest, element.currentTime || 0);
    if (element.ended) seen.ended = true;
    const at = tenths(seen.ended ? length : Math.min(seen.furthest, length));
    if (at <= seen.toldAt && seen.ended === seen.toldEnded) return;
    seen.toldAt = at;
    seen.toldEnded = seen.ended;
    reportViewing(id, { at, length: tenths(length), ended: seen.ended }, leaving);
  }, []);

  useEffect(() => { onReadyChange?.(ready && !failed); }, [ready, failed, onReadyChange]);

  useEffect(() => {
    if (!feed) return;
    const preference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!preference) return;
    const update = () => {
      if (preference.matches) playbackChoice.current = null;
      setReducedMotion(preference.matches);
    };
    update();
    preference.addEventListener?.("change", update);
    return () => preference.removeEventListener?.("change", update);
  }, [feed]);

  // Removing the attribute alone would let the download run on; load() resets
  // the element. This also releases an element that is about to be removed,
  // which is a stop: the element still knows how far the viewer got.
  useEffect(() => {
    const element = player.current;
    if (!element || !hold) {
      setReady(false);
      return;
    }
    element.setAttribute("src", source);
    return () => {
      tell(element);
      element.removeAttribute("src");
      element.load();
    };
  }, [hold, source, retry, failed, tell]);

  // The page hidden or closed is a stop too; its report goes at once and outlives the page.
  useEffect(() => {
    const hidden = () => { if (window.document.visibilityState === "hidden") tell(player.current, true); };
    const closed = () => tell(player.current, true);
    window.document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", closed);
    return () => {
      window.document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", closed);
    };
  }, [tell]);

  const startPlayback = useCallback((element: HTMLMediaElement) => {
    const attempt = ++playbackAttempt.current;
    const current = () => attempt === playbackAttempt.current && player.current === element;
    const start = () => Promise.resolve(element.play());
    const started = () => { if (current()) { setHeld(false); setPaused(false); } };
    const refused = (failure: unknown) => (failure as { name?: string } | null)?.name === "NotAllowedError";
    start().then(started, failure => {
      if (!current() || !refused(failure)) return;
      if (element.muted) {
        setHeld(true);
        return;
      }
      // This browser wants a tap before sound: keep the film going, silent.
      element.muted = true;
      setMuted(true);
      start().then(started, () => { if (current()) setHeld(true); });
    });
  }, []);

  // Sound is independent of playback: changing it must not resume a paused film.
  useEffect(() => {
    if (!feed || !player.current) return;
    player.current.muted = !sound;
    setMuted(!sound);
  }, [feed, sound, retry, failed]);

  // Only the visible current work may play. Reduced motion defaults to a still cover.
  useEffect(() => {
    const element = player.current;
    if (!feed || !element || !hold) return;
    if (playing && (playbackChoice.current ?? !reducedMotion)) startPlayback(element);
    else { element.pause(); setPaused(true); }
    return () => { playbackAttempt.current++; };
  }, [feed, hold, playing, reducedMotion, retry, failed, startPlayback]);

  const togglePlayback = () => {
    const element = player.current;
    if (!element || !hold || !playing) return;
    const start = paused || held;
    playbackChoice.current = start;
    if (start) startPlayback(element);
    else { playbackAttempt.current++; element.pause(); setPaused(true); }
  };

  const toggleSound = () => {
    const element = player.current;
    if (!element || !hold) return;
    const on = element.muted;
    element.muted = !on;
    setMuted(!on);
    onSound?.(on);
  };

  if (!source) return <ErrorState error={new Error("作品没有提供可打开的入口，请重新准备预览。")} />;
  const media = {
    ref: bind,
    preload: "metadata",
    playsInline: true,
    controls: !feed,
    onPlay: () => { setPaused(false); setHeld(false); },
    onLoadedMetadata: () => setReady(true),
    onError: (event: SyntheticEvent<HTMLMediaElement>) => {
      if (event.currentTarget.getAttribute("src")) setFailed(true);
    },
    onVolumeChange: (event: SyntheticEvent<HTMLMediaElement>) => setMuted(event.currentTarget.muted),
    onTimeUpdate: (event: SyntheticEvent<HTMLMediaElement>) => {
      const seen = watched.current;
      seen.furthest = Math.max(seen.furthest, event.currentTarget.currentTime);
      // The first stop comes by itself, once the film has played a little.
      if (!seen.toldAt && !seen.toldEnded && seen.furthest >= PLAYED_A_LITTLE) tell(event.currentTarget);
    },
    onPause: (event: SyntheticEvent<HTMLMediaElement>) => { setPaused(true); tell(event.currentTarget); },
    onEnded: (event: SyntheticEvent<HTMLMediaElement>) => {
      const element = event.currentTarget;
      watched.current.ended = true;
      tell(element);
      // The feed plays a film round again by hand rather than by `loop`, which
      // would hide the end from the player.
      if (feed && playing && (playbackChoice.current ?? !reducedMotion)) {
        element.currentTime = 0;
        startPlayback(element);
      }
    },
  };
  return <section className={`public-media is-${mode}`} aria-label={publication.title}>
    {failed ? (
      <WorkProblem
        message={mode === "preview" ? "媒体暂时无法加载，请重新准备预览。" : "无法播放。"}
        onRetry={() => {
          setFailed(false);
          setRetry(value => value + 1);
        }}
      />
    ) : (
      <div className="public-media-stage" onClick={feed ? togglePlayback : undefined}>
        {kind === "video" ? (
          <video key={retry} {...media} poster={mode === "preview" ? undefined : publication.cover_url} />
        ) : (
          <>
            {feed ? publication.cover_url ? <img src={publication.cover_url} alt="" /> : <Icon name="sound" size={40} />
              : null}
            <audio key={retry} {...media} />
          </>
        )}
        {feed && (held || paused) ? <span className="public-media-held" aria-hidden="true"><Icon name="play" size={46} /></span>
          : null}
      </div>
    )}
    {/* Films carry their own subtitles and labels; their controls sit below the picture. */}
    {feed && !failed && controls ? createPortal(
      <><button type="button" className="public-media-play" disabled={!playing || !hold} onClick={togglePlayback}>
        {paused || held ? "播放" : "暂停"}
      </button><button
        type="button"
        className="public-media-sound"
        aria-label="声音"
        aria-pressed={!muted}
        onClick={toggleSound}
      >
        <Icon name={muted ? "muted" : "volume"} size={20} />
      </button></>,
      controls,
    ) : null}
    {mode === "preview" && !published ? <p className="public-surface-storage">预览 · 不会公开，也不产生用量</p> : null}
  </section>;
}

type Progress = {
  state: unknown;
  version: number;
  identity: string;
  storage: "browser" | "account";
  migration?: { guest_state: unknown; guest_version: number; account_version: number }
};
const localDrafts = new Map<string, unknown>();
type Document = { html: string; progress: Progress; mediaOrigin?: string };
type Action = {
  command_id: string;
  obligation_id: string | null;
  action: string;
  params: unknown;
  state: string;
  turn_id?: string;
  text?: string;
  error?: string
};

function readProgress(path: string, signal: AbortSignal): Promise<Progress> {
  // The first read may set an HttpOnly visitor cookie. It takes its turn with
  // every other request that may, in this tab and any other.
  return asVisitor(() => {
    signal.throwIfAborted();
    return new Promise<Progress>((resolve, reject) => {
      const cancel = () => reject(new DOMException("Aborted", "AbortError"));
      signal.addEventListener("abort", cancel, { once: true });
      platformRequest<Progress>(path, { signal }).then(resolve, reject)
        .finally(() => signal.removeEventListener("abort", cancel));
    });
  }, signal);
}

// A base URL resolves published assets, but must not turn a local #anchor into
// a navigation to that asset directory. Keep these document behaviours generic.
const documentHelpers = `<script>
new ResizeObserver(function () {
  parent.postMessage({__pa: 1, method: 'public.resize', params: {
    height: Math.ceil(document.body.getBoundingClientRect().height)
  }}, '*');
}).observe(document.body);
function checkpoint() {
  var fields = Array.from(document.querySelectorAll('input:not([type="password"]):not([type="file"]),textarea,select')).map(function(el, i) {
    return {key: el.id || el.name || String(i), value: el.value, checked: el.checked};
  });
  var media = Array.from(document.querySelectorAll('video,audio')).map(function(el) {return el.currentTime || 0;});
  parent.postMessage({__pa:1, method:'public.checkpoint', params:{fields:fields,media:media}}, '*');
}
document.addEventListener('input', checkpoint);
document.addEventListener('change', checkpoint);
document.addEventListener('timeupdate', checkpoint, true);
window.addEventListener('message', function(event) {
  if (event.source !== parent || !event.data || event.data.__pa !== 1 || event.data.method !== 'public.restore') return;
  var saved = event.data.params || {};
  Array.from(document.querySelectorAll('input:not([type="password"]):not([type="file"]),textarea,select')).forEach(function(el, i) {
    var held = (saved.fields || []).find(function(item) {return item.key === (el.id || el.name || String(i));});
    if (!held || (el.value === held.value && (typeof held.checked !== 'boolean' || el.checked === held.checked))) return;
    el.value = held.value; if (typeof held.checked === 'boolean') el.checked = held.checked;
    el.dispatchEvent(new Event('input', {bubbles:true}));
  });
  Array.from(document.querySelectorAll('video,audio')).forEach(function(el, i) {
    if (saved.media && Number.isFinite(saved.media[i])) el.currentTime = saved.media[i];
  });
});
document.addEventListener('click', function (event) {
  var anchor = event.target.closest && event.target.closest('a[href^="#"]');
  if (!anchor) return;
  var target;
  try { target = document.getElementById(decodeURIComponent(anchor.getAttribute('href').slice(1))); }
  catch (_) { return; }
  if (target) { event.preventDefault(); target.scrollIntoView(); }
});
</script>`;

/** A public document uses the Liquid bridge but receives no Owner capability. */
function PublicDocument({
  publication, mode = "page", visible = true, previewAssetBase, published = false, onReadyChange,
}: SurfaceProps) {
  const preview = mode === "preview";
  // A Square card has a fixed size: the page fills it instead of growing to its own height.
  const feed = mode === "feed";
  const frame = useRef<HTMLIFrameElement>(null);
  const appearance = useAppearance();
  const [document, setDocument] = useState<Document | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pageError, setPageError] = useState("");
  const [actionError, setActionError] = useState("");
  const rereadActions = useRef<() => void>(() => {});
  const [height, setHeight] = useState(640);
  const [retry, setRetry] = useState(0);
  const [rendered, setRendered] = useState(false);
  const region = useRef<HTMLElement>(null);
  useContentMotion(region, publication.id, visible && rendered);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const base = `/api/public/works/${encodeURIComponent(publication.id)}`;
  const assetPath = preview ? previewAssetBase : `${base}/assets/`;

  useEffect(() => {
    onReadyChange?.(Boolean(document && rendered && !error && !pageError));
  }, [document, rendered, error, pageError, onReadyChange]);

  useEffect(() => {
    const abort = new AbortController();
    setDocument(null); setError(null); setPageError(""); setActionError(""); setRendered(false);
    const entry = publication.entry;
    if (!entry || !assetPath) {
      setError(new Error("作品没有提供可打开的入口，请重新准备预览。"));
      return;
    }
    Promise.all([
      fetch(`${assetPath}${entry}`, { signal: abort.signal }).then(async response => {
        if (!response.ok) throw new Error("作品不存在、已下架或文件暂时无法读取。");
        return { html: await response.text(), mediaOrigin: mediaOrigin(response.headers) };
      }),
      preview
        ? Promise.resolve({ state: null, version: 0, identity: "preview", storage: "browser" as const })
        : readProgress(`${base}/state`, abort.signal),
    ]).then(([page, progress]) => {
      if (!abort.signal.aborted) setDocument({ ...page, progress });
    }).catch(failure => { if (!abort.signal.aborted) setError(failure); });
    return () => abort.abort();
  }, [base, assetPath, publication.entry, retry, preview]);

  // Install the bridge before the iframe can send its initial ready message.
  useLayoutEffect(() => {
    if (!document) return;
    const abort = new AbortController();
    let progress = { ...document.progress };
    const draftKey = `${base}:${progress.identity}`;
    let restoredDraft = false;
    const actionStates: Record<string, Action> = {};
    let events: EventSource | null = null;
    const push = () =>
      frame.current?.contentWindow?.postMessage({ __pa: 1, method: "surface.state", params: snapshot() }, "*");
    const pending = (action: Action) => action.state === "running" || (action.state === "unknown" && !action.turn_id);
    const apply = (...actions: Action[]) => {
      actions.forEach(action => { actionStates[action.command_id] = action; });
      push();
      if (!Object.values(actionStates).some(pending)) { events?.close(); events = null; }
    };
    const listen = (restart = false) => {
      if (restart) { events?.close(); events = null; }
      if (abort.signal.aborted || preview || !publication.actions?.length || events) return;
      const stream = new EventSource(`${base}/actions/events`);
      events = stream;
      stream.onmessage = event => { try { apply(JSON.parse(event.data)); } catch { return; } };
      // CONNECTING retries itself. CLOSED may be an authorization refusal;
      // leave it closed until the person explicitly checks the original work.
      stream.onerror = () => {
        if (abort.signal.aborted || events !== stream || stream.readyState !== EventSource.CLOSED) return;
        events = null;
        setActionError("暂时读不到最新结果。");
      };
      // Reopen reads the original receipts, including results from the outage.
      stream.onopen = () => { void readActions(false).catch(() => {}); };
    };
    const readActions = async (connect = true) => {
      if (preview || !publication.actions?.length) return;
      try {
        const before = { ...actionStates };
        const result = await platformRequest<{ actions: Action[] }>(`${base}/actions`, { signal: abort.signal });
        if (abort.signal.aborted) return;
        // Fold the whole batch before deciding whether any work still needs
        // the stream. A live receipt that overtook this read remains later.
        apply(...result.actions.filter(action => actionStates[action.command_id] === before[action.command_id]));
        const unfinished = Object.values(actionStates).some(pending);
        if (connect || !unfinished || events) setActionError("");
        if (connect && unfinished) listen();
      } catch (failure) {
        if (!abort.signal.aborted) setActionError("暂时读不到最新结果。");
        throw failure;
      }
    };
    rereadActions.current = () => { void readActions().catch(() => {}); };
    let saveQueue = Promise.resolve();
    let queued = 0;
    let unconfirmed: { state: unknown; body: string } | null = null;
    const persist = async (write: { state: unknown; body: string }) => {
      if (preview) {
        progress = { ...progress, state: write.state, version: progress.version + 1 };
        unconfirmed = null;
        return { saved: true, version: progress.version };
      }
      const result = await platformRequest<{ saved: boolean; version: number }>(`${base}/state`, {
        method: "PUT", signal: abort.signal, body: write.body,
      });
      if (!result.saved || !Number.isInteger(result.version))
        throw new Error("还没有收到完整的保存结果，请重试。");
      progress = { ...progress, state: write.state, version: result.version };
      unconfirmed = null;
      return result;
    };
    const snapshot = () => ({
      context: { publication_id: publication.id },
      theme: effectiveTheme(),
      palette: window.document.documentElement.dataset.palette,
      motion: window.document.documentElement.dataset.motion || "full",
      reads: [],
      actions: actionStates,
      drafts: {},
      visible: visibleRef.current && !window.document.hidden
    });
    const incoming = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.data?.__pa !== 1) return;
      const data = event.data;
      if (data.method === "public.checkpoint") {
        try {
          if (JSON.stringify(data.params).length <= 32768) {
            localDrafts.delete(draftKey); localDrafts.set(draftKey, data.params);
            if (localDrafts.size > 100) localDrafts.delete(localDrafts.keys().next().value!);
          }
        } catch { return; }
        return;
      }
      if (data.method === "public.resize") {
        if (Number.isFinite(data.params?.height)) setHeight(Math.max(360, Math.min(12000, data.params.height)));
        return;
      }
      if (typeof data.id !== "string" || data.id.length > 120) return;
      const reply = (result?: unknown, failure?: string) => {
        if (!abort.signal.aborted) frame.current?.contentWindow?.postMessage({ __pa: 1, id: data.id,
          ...(failure ? { error: failure } : { result }) }, "*");
      };
      switch (data.method) {
        case "surface.ready":
          reply({
            ...snapshot(),
            ui_state: progress.state,
            ui_state_version: progress.version,
            scope: publication.id,
            storage: progress.storage,
            pending: Object.values(actionStates),
            capabilities: { persist: !preview, tools: false, agent: !preview && Boolean(publication.actions?.length) }
          });
          break;
        case "surface.subscribe":
        case "surface.refresh":
          void readActions().then(() => reply(snapshot())).catch(failure => reply(undefined, String(failure)));
          break;
        case "surface.rendered":
          setRendered(true); reply({ recorded: true });
          if (!restoredDraft) {
            restoredDraft = true;
            if (localDrafts.has(draftKey))
              frame.current?.contentWindow?.postMessage(
                { __pa: 1, method: "public.restore", params: localDrafts.get(draftKey) },
                "*"
              );
          }
          break;
        case "surface.error":
          setPageError(
            String(data.params?.detail?.message || data.params?.message || "作品遇到一个运行错误。").slice(0, 300)
          );
          reply({ recorded: true });
          break;
        case "surface.act": {
          if (preview) {
            reply(undefined, "预览中不会调用 Agent，公开后访客登录即可使用。");
            break;
          }
          if (!publication.actions?.some(item => item.id === data.params?.tool)) {
            reply(undefined, "作品没有开放这个动作。");
            break;
          }
          // The page names its press; the Hub names a public request itself and
          // reads a name it is sent as "find that one", so the name stays here.
          const { command_id: _named, ...press } = data.params ?? {};
          void platformRequest<Action>(`${base}/actions`, {
            method: "POST",
            signal: abort.signal,
            body: JSON.stringify(press)
          })
            .then(result => {
              apply(result);
              reply(result);
              if (pending(result)) listen(true);
            })
            .catch(() => reply(undefined, "没有完成。"));
          break;
        }
        case "surface.save_state": {
          // Serialize saves from this mounted page. Server CAS also protects other
          // tabs, session changes, and a lost reply. Never replace the page's draft.
          const state = data.params?.state ?? null;
          let size: number;
          try { size = new TextEncoder().encode(JSON.stringify(state)).byteLength; }
          catch { reply(undefined, "进度需要是可保存的 JSON 数据。"); break; }
          if (queued >= 4 || size > 32768) {
            reply(undefined, "请等当前保存结束；进度最多可保存 32 KB。");
            break;
          }
          queued++;
          saveQueue = saveQueue.then(async () => {
            if (abort.signal.aborted) return;
            try {
              if (unconfirmed) {
                // A lost reply may already have committed. Confirm that exact
                // write before submitting newer input with its resulting version.
                const earlier = unconfirmed;
                const result = await persist(earlier);
                if (JSON.stringify(state) === JSON.stringify(earlier.state)) { reply(result); return; }
              }
              unconfirmed = {
                state,
                body: JSON.stringify({ state, expected_version: progress.version, identity: progress.identity })
              };
              reply(await persist(unconfirmed));
            } catch {
              reply(undefined, "进度没有保存。");
            } finally { queued--; }
          });
          break;
        }
        default:
          reply(undefined, "公开作品没有开放这个能力；私有工具和 IP 对话属于独立权限。");
      }
    };
    window.addEventListener("message", incoming);
    return () => {
      abort.abort(); events?.close(); rereadActions.current = () => {};
      window.removeEventListener("message", incoming);
    };
  }, [base, document, publication.id, publication.actions, preview]);

  useEffect(() => {
    const send = () => frame.current?.contentWindow?.postMessage({ __pa: 1, method: "surface.theme",
      params: { theme: effectiveTheme(), palette: window.document.documentElement.dataset.palette, motion: window.document.documentElement.dataset.motion || "full" } }, "*");
    send();
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", send);
    return () => media.removeEventListener("change", send);
  }, [appearance.value]);
  useEffect(() => {
    const send = () => frame.current?.contentWindow?.postMessage({ __pa: 1, method: "surface.visibility",
      params: { visible: visible && !window.document.hidden } }, "*");
    send();
    window.document.addEventListener("visibilitychange", send);
    return () => window.document.removeEventListener("visibilitychange", send);
  }, [visible]);

  const srcDoc = useMemo(() => {
    if (!document) return "";
    const assetBase = `${location.origin}${assetPath}`;
    // Size and theme updates do not replace the document or its unsaved inputs.
    return buildSrcdoc({
      panelKey: publication.id,
      blockIndex: 0,
      html:
        document.html.replace(/((?:src|href)=["'])(?:\.\.\/|\.\/)*_pa\//g, `$1${assetBase}_pa/`) + documentHelpers,
      ui: assetBase + (
        publication.entry?.includes("/") ? publication.entry.slice(0, publication.entry.lastIndexOf("/") + 1) : ""
      ),
      assetRoot: assetBase,
      media: assetBase,
      mediaOrigin: document.mediaOrigin,
      theme: effectiveTheme(),
      palette: window.document.documentElement.dataset.palette,
      motion: window.document.documentElement.dataset.motion || "full"
    });
  }, [document, assetPath, publication.entry, publication.id]);
  const importProgress = async (choice: "guest" | "account") => {
    if (!document?.progress.migration) return;
    try {
      await platformRequest(`${base}/state/import`, { method: "POST",
        body: JSON.stringify({ choice, ...document.progress.migration }) });
      setRetry(value => value + 1);
    } catch (failure) { setError(failure); }
  };
  if (error) return <ErrorState error={error} onRetry={() => setRetry(v => v + 1)} />;
  if (!document) return <LoadingState label="正在打开作品" />;
  return <section ref={region} className={`public-surface${feed ? " is-feed" : ""}`} aria-label={publication.title}>
    {document.progress.migration ? (
      <div className="public-surface-error" role="status">
        <p>此浏览器和账号各有一份进度，选择要保留的版本。</p>
        <button className="btn" onClick={() => void importProgress("guest")}>继续此浏览器的进度</button>
        <button className="btn" onClick={() => void importProgress("account")}>保留账号的进度</button>
      </div>
    ) : null}
    {actionError ? <div className="public-surface-error" role="alert">
      <p>{actionError}</p>
      <button type="button" className="btn" data-action="reread" onClick={() => rereadActions.current()}>重新读取</button>
    </div> : null}
    {pageError ? (
      <WorkProblem
        message={pageError}
        onRetry={() => {
          setRendered(false);
          setRetry(v => v + 1);
        }}
      />
    ) : null}
    {!rendered && !pageError ? (
      <p className="micro public-surface-loading" role="status">正在绘制作品…</p>
    ) : null}
    <iframe
      key={`${publication.id}:${retry}`}
      ref={frame}
      title={publication.title}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      style={feed ? undefined : { height }}
    />
    {feed ? null : <>
      {!preview && publication.actions?.length ? (
        <p className="public-surface-storage">登录后可让 IP 回应 · 每小时最多 20 次</p>
      ) : null}
      {preview && published ? null : (
        <p className="public-surface-storage">
          {preview
            ? "预览 · 不会公开，也不产生用量"
            : document.progress.storage === "account"
              ? "进度保存到当前账号"
              : "无需登录即可体验 · 进度保留 30 天，登录后可带到账号"}
        </p>
      )}
    </>}
  </section>;
}
